// ─── Indeks HADIAH PEMBELIAN per toko — helper MURNI (tanpa I/O) ────────────
//
// Rancangan: docs/RENCANA-hadiah-pembelian.md §13.3 (Tahap 2A, H18). Satu
// indeks ringan per toko — aturan yang SEDANG BERJALAN beserta produk syarat &
// hadiahnya, varian hadiah, dan sisa kuota total — dibaca server SEKALI
// (`fetchActiveGiftIndexServer`, cache ~60 dtk) lalu diedarkan lewat context
// ke chip kartu produk, seksi "Hadiah Pembelian" di `/promo`, dan blok "Gratis
// hadiah" halaman produk. Nol query per kartu.
//
// Bentuknya SERIALISABLE (tanpa Map) karena melintasi batas server → klien
// sebagai props; `giftIndexToData` menyusunnya jadi `StorefrontGiftData` yang
// dipakai helper keranjang (`productGiftOffers`).

import {
  giftOwnerCandidates,
  type GiftProductRef,
  type GiftQuota,
  type GiftRewardVariant,
  type GiftRule
} from "@/lib/gift";
import {
  EMPTY_GIFT_DATA,
  giftRuleOffer,
  productGiftOffers,
  type GiftProductInfo,
  type StorefrontGiftData
} from "@/lib/giftCart";
import { resolveProductMediaUrl } from "@/lib/mediaUrl";
import { buildBrandLink, buildCategoryLink, buildProductLink } from "@/lib/popups";

/**
 * Kolom produk syarat/hadiah yang memang di-grant ke anon (cermin
 * `PRODUCT_DETAIL_SELECT`, `public-data.ts`): kolom tak ber-grant = PostgREST
 * menolak SELURUH query. Satu string untuk pembaca klien (`supabase/gifts.ts`)
 * dan server (`supabase/giftServer.ts`).
 */
export const GIFT_PRODUCT_SELECT =
  "id, slug, name, product_type, status, brand, category, product_media(media_type, media_url, storage_path, is_cover), variant_groups(id, sort_order, variant_options(id, name, sort_order))";

/** Kolom varian produk hadiah (anon, RLS toko = varian aktif produk aktif). */
export const GIFT_VARIANT_SELECT = "id, product_id, name, option_ids, price_extra, stock, sort_order";

/** Plafon varian produk hadiah yang dimuat (hadiah lazimnya barang kecil berbilang varian sedikit). */
export const GIFT_VARIANT_CAP = 500;

const bySort = (a: { sort_order?: unknown }, b: { sort_order?: unknown }) =>
  (Number(a?.sort_order) || 0) - (Number(b?.sort_order) || 0);

/** Baris `products` (+ grup & opsi varian, media) → produk syarat/hadiah. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapGiftProduct(row: any): GiftProductInfo {
  const optionGroupOf = new Map<string, string>();
  const optionNameOf = new Map<string, string>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  for (const g of ((row?.variant_groups as any[]) ?? []).slice().sort(bySort)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const o of ((g?.variant_options as any[]) ?? []).slice().sort(bySort)) {
      optionGroupOf.set(String(o.id), String(g.id));
      optionNameOf.set(String(o.id), String(o.name ?? "").trim());
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const media = ((row?.product_media as any[]) ?? []).filter((m) => m?.media_type === "image");
  const cover = media.find((m) => m.is_cover) ?? media[0] ?? null;
  return {
    id: String(row?.id ?? ""),
    slug: String(row?.slug ?? ""),
    name: String(row?.name ?? "").trim(),
    productType: row?.product_type === "preorder" ? "preorder" : "ready_stock",
    status: String(row?.status ?? ""),
    imageUrl: cover ? resolveProductMediaUrl(cover) ?? null : null,
    brand: typeof row?.brand === "string" && row.brand.trim() ? row.brand : null,
    category: typeof row?.category === "string" && row.category.trim() ? row.category : null,
    optionGroupOf,
    optionNameOf,
    variants: []
  };
}

/** Baris `product_variants` produk hadiah → varian hadiah (stok hanya Ready Stock). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapGiftRewardVariant(row: any, productType: GiftProductInfo["productType"]): GiftRewardVariant {
  return {
    id: String(row?.id ?? ""),
    name: String(row?.name ?? "").trim() || "Standar",
    optionIds: Array.isArray(row?.option_ids) ? row.option_ids.map(String) : [],
    priceExtra: Math.max(0, Math.round(Number(row?.price_extra) || 0)),
    // Stok hanya membatasi Ready Stock; kuota PO diperiksa server (GIFT_INVALID:stock).
    stock: productType === "ready_stock" ? Math.max(0, Math.floor(Number(row?.stock) || 0)) : null
  };
}

// ─── Bentuk serialisable ────────────────────────────────────────────────────

export type GiftIndexProduct = Omit<GiftProductInfo, "optionGroupOf" | "optionNameOf"> & {
  /** Opsi varian urut grup & opsi produk: [id opsi, id grup, nama opsi]. */
  options: Array<[optionId: string, groupId: string, name: string]>;
};

export type GiftIndex = {
  /**
   * SEMUA aturan aktif & dalam periode saat dibaca, urut nama — termasuk yang
   * hadiahnya tak terbaca (pemilik baris H24 dinilai atas semuanya). Level
   * dinilai klien.
   */
  rules: GiftRule[];
  products: GiftIndexProduct[];
  /** Sisa kuota TOTAL aturan berkuota — jatah per pembeli tak dikenal server (anon). */
  quotas: Array<[ruleId: string, quota: GiftQuota]>;
};

export const EMPTY_GIFT_INDEX: GiftIndex = { rules: [], products: [], quotas: [] };

export function giftIndexFromData(data: StorefrontGiftData): GiftIndex {
  return {
    rules: data.rules,
    products: [...data.products.values()].map(({ optionGroupOf, optionNameOf, ...rest }) => ({
      ...rest,
      options: [...optionGroupOf.entries()].map(
        ([id, groupId]) => [id, groupId, optionNameOf.get(id) ?? ""] as [string, string, string]
      )
    })),
    quotas: [...data.quotas.entries()]
  };
}

export function giftIndexToData(index: GiftIndex | null | undefined): StorefrontGiftData {
  if (!index?.rules?.length) return EMPTY_GIFT_DATA;
  const products = new Map<string, GiftProductInfo>();
  for (const { options, ...rest } of index.products ?? []) {
    products.set(rest.id, {
      ...rest,
      optionGroupOf: new Map(options.map(([id, groupId]) => [id, groupId])),
      optionNameOf: new Map(options.map(([id, , name]) => [id, name]))
    });
  }
  return { rules: index.rules, products, variantOptionIds: new Map(), quotas: new Map(index.quotas ?? []) };
}

// ─── Turunan layar ──────────────────────────────────────────────────────────

/** Aturan "semua produk" tak memasang chip (H29) — semua kartu akan berchip. */
function giftRuleChips(rule: GiftRule): boolean {
  return !(rule.triggerScope === "target" && (rule.triggerTargetType ?? "all") === "all");
}

/**
 * Chip "Gratis hadiah" untuk kartu produk ini bagi pembeli ber-slot ini (H18,
 * H29): ada tawaran hadiah yang sama dengan blok halaman produk, dari aturan
 * produk tunggal atau bertarget produk / kategori / merek — dicocokkan dari
 * merek, kategori, dan tipe yang sudah ada di data kartu (nol fetch per kartu).
 * `candidates` = `giftOwnerCandidates` yang dihitung sekali per halaman.
 */
export function giftChipFor(
  data: StorefrontGiftData,
  candidates: readonly GiftRule[],
  product: GiftProductRef
): boolean {
  if (!data.rules.length) return false;
  return productGiftOffers(data, product, null, new Date(0), candidates).some((o) => giftRuleChips(o.rule));
}

export type GiftPromoCard = {
  ruleId: string;
  sentence: string;
  /** Produk syarat / halaman kategori / merek bila targetnya satu; null = kartu tanpa tautan. */
  href: string | null;
  reward: { name: string; imageUrl: string | null };
  remaining: number | null;
};

/**
 * Tautan kartu `/promo`: produk syarat (produk tunggal, atau target satu
 * produk), halaman kategori / merek bila targetnya satu; selain itu null.
 */
export function giftPromoHref(rule: GiftRule, products: ReadonlyMap<string, GiftProductInfo>): string | null {
  if (rule.triggerScope !== "target") {
    const trigger = rule.triggerProductId ? products.get(rule.triggerProductId) : undefined;
    return trigger?.slug ? buildProductLink(trigger.slug) : null;
  }
  const include = rule.triggerTargets.filter((t) => t.mode !== "exclude" && t.kind === rule.triggerTargetType);
  if (include.length !== 1) return null;
  const [only] = include;
  if (only.kind === "category") return buildCategoryLink(only.name);
  if (only.kind === "brand") return buildBrandLink(only.name);
  const product = only.productId ? products.get(only.productId) : undefined;
  return product?.slug ? buildProductLink(product.slug) : null;
}

/** Kartu seksi "Hadiah Pembelian" di `/promo`: satu per aturan berjalan & berhak yang hadiahnya bisa diberikan sekarang. */
export function giftPromoCards(data: StorefrontGiftData, slot: number | null, now: Date): GiftPromoCard[] {
  const out: GiftPromoCard[] = [];
  for (const rule of giftOwnerCandidates(data.rules, { slot, now })) {
    const offer = giftRuleOffer(data, rule);
    if (!offer) continue;
    out.push({
      ruleId: rule.id,
      sentence: offer.sentence,
      href: giftPromoHref(rule, data.products),
      reward: { name: offer.reward.name, imageUrl: offer.reward.imageUrl },
      remaining: offer.remaining
    });
  }
  return out.sort((a, b) => a.sentence.localeCompare(b.sentence, "id"));
}
